import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import Quill from 'quill';
import { ObservationCustomFieldsService } from './observation-custom-fields.service';

export interface EditorLimits {
  maxTextLength: number;
  maxHtmlLength: number;
}

export interface EditorConfig {
  nwcClient: boolean;
  observationType: string;
  form: FormGroup;
  observationCustomFields: ObservationCustomFieldsService;
}

@Injectable({
  providedIn: 'root',
})
export class QuillEditorService {
  // Truncation management properties
  shouldTruncateAfterPaste: boolean = false;
  pendingTruncationLength: number = 0;
  // Track last known selection for each editor
  private lastSelection: Map<Quill, { index: number; length: number }> = new Map();

  constructor() {}

  /**
   * Get text length limits for description editor
   */
  getDescriptionTextLimits(config: EditorConfig): number {
    let maxLength = config.nwcClient ? 2000 : 1250;

    const type = ((config?.form?.get('type')?.value as string) || config.observationType || '')
      .toString()
      .toUpperCase();
    const nwcTypes = config.observationCustomFields.getNWCOnlyCategoryTypes();

    if (config.nwcClient && nwcTypes.includes(type)) {
      if (type === 'RFI') {
        maxLength = 2000; // Text limit for RFI type
      } else {
        maxLength = 4000; // Text limit for other NWC types
      }
    }

    return maxLength;
  }

  /**
   * Get HTML length limits for description editor
   */
  getDescriptionHtmlLimits(config: EditorConfig): number {
    let maxLength = config.nwcClient ? 8000 : 5000;

    const type = ((config?.form?.get('type')?.value as string) || config.observationType || '')
      .toString()
      .toUpperCase();
    if (
      config.nwcClient &&
      config.observationCustomFields.getNWCOnlyCategoryTypes().includes(type)
    ) {
      if (type === 'RFI') {
        maxLength = 8000; // HTML limit for RFI type
      } else {
        maxLength = 16000; // HTML limit for other NWC types
      }
    }

    return maxLength;
  }

  /**
   * Get text length limits for recommended action editor
   */
  getRecommendedActionTextLimits(config: EditorConfig): number {
    let maxLength = config.nwcClient ? 2000 : 1250;

    const type = ((config?.form?.get('type')?.value as string) || config.observationType || '')
      .toString()
      .toUpperCase();
    if (
      config.nwcClient &&
      config.observationCustomFields.getNWCOnlyCategoryTypes().includes(type)
    ) {
      maxLength = 2000; // Text limit for other NWC types
    }

    return maxLength;
  }

  /**
   * Get HTML length limits for recommended action editor
   */
  getRecommendedActionHtmlLimits(config: EditorConfig): number {
    let maxLength = config.nwcClient ? 8000 : 5000;

    const type = ((config?.form?.get('type')?.value as string) || config.observationType || '')
      .toString()
      .toUpperCase();
    if (
      config.nwcClient &&
      config.observationCustomFields.getNWCOnlyCategoryTypes().includes(type)
    ) {
      maxLength = 8000; // HTML limit for other NWC types
    }

    return maxLength;
  }

  /**
   * Get limits for a specific editor type
   */
  getEditorLimits(config: EditorConfig, type: 'description' | 'recommendedAction'): EditorLimits {
    const maxTextLength =
      type === 'description'
        ? this.getDescriptionTextLimits(config)
        : this.getRecommendedActionTextLimits(config);

    const maxHtmlLength =
      type === 'description'
        ? this.getDescriptionHtmlLimits(config)
        : this.getRecommendedActionHtmlLimits(config);

    const limits = {
      maxTextLength,
      maxHtmlLength,
    };

    return limits;
  }

  /**
   * Setup editor with all event handlers
   */
  setupEditor(
    quill: Quill,
    editorType: 'description' | 'recommendedAction',
    config: EditorConfig,
  ): void {
    // Ensure quill is properly initialized
    if (!quill || typeof quill.getText !== 'function') {
      console.warn('Quill editor not properly initialized:', quill);
      return;
    }

    quill.blur();

    // Handle Arabic text formatting
    if (this.isArabic()) {
      quill.format('font', 'mirza');
      quill.format('align', 'right');
      quill.format('direction', 'rtl');
    }

    // Remove previous handlers if they exist to avoid duplicate listeners with stale limits
    const anyEditor: any = quill as any;
    if (anyEditor.__whHandlers) {
      try {
        quill.root?.removeEventListener('paste', anyEditor.__whHandlers.paste);
        quill.root?.removeEventListener('keydown', anyEditor.__whHandlers.keydown);
        quill.off('text-change', anyEditor.__whHandlers.textChange);
        quill.off('selection-change', anyEditor.__whHandlers.selectionChange);
      } catch {}
    }

    const pasteHandler = this.createPasteHandler(quill, editorType, config);
    const textChangeHandler = this.createTextChangeHandler(quill, editorType, config);
    const keydownHandler = this.createKeydownHandler(quill, editorType, config);
    const selectionChangeHandler = this.createSelectionChangeHandler(quill);

    // Override Quill's clipboard module to prevent it from interfering with our paste handler
    // This must be done BEFORE adding our paste listener
    const clipboard = (quill as any).getModule('clipboard');
    if (clipboard) {
      // Store original onPaste if we need to restore it later
      const originalOnPaste = clipboard.onPaste;
      // Override with a no-op to prevent Quill's default paste behavior
      clipboard.onPaste = (e: ClipboardEvent) => {
        // Do nothing - our custom paste handler will handle everything
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      // Save reference for cleanup
      (quill as any).__originalClipboardOnPaste = originalOnPaste;
    }

    // Add event listeners with capture and once per paste
    quill.root?.addEventListener('paste', pasteHandler, { capture: true });
    quill.root?.addEventListener('keydown', keydownHandler);
    quill.on('text-change', textChangeHandler);
    quill.on('selection-change', selectionChangeHandler);

    // Save handler references for future cleanup/re-init
    (quill as any).__whHandlers = {
      paste: pasteHandler,
      keydown: keydownHandler,
      textChange: textChangeHandler,
      selectionChange: selectionChangeHandler,
    };
  }

  /**
   * Create selection change handler to track cursor position
   */
  private createSelectionChangeHandler(editor: Quill) {
    return (range: any) => {
      if (range) {
        this.lastSelection.set(editor, { index: range.index, length: range.length });
      }
    };
  }

  /**
   * Create keydown handler to prevent input at character limits
   */
  private createKeydownHandler(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ) {
    return (e: KeyboardEvent) => {
      const limits = this.getEditorLimits(config, type);
      // Quill getLength() includes a trailing newline; use effective length for limits
      const effectiveTextLength = Math.max(0, editor.getLength() - 1);
      const currentHtmlLength = editor.root.innerHTML.length;

      // Allow navigation and editing keys
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown',
        'Tab',
        'Enter',
        'Escape',
        'Control',
        'Alt',
        'Meta',
        'Shift',
        'F1',
        'F2',
        'F3',
        'F4',
        'F5',
        'F6',
        'F7',
        'F8',
        'F9',
        'F10',
        'F11',
        'F12',
      ];

      // Allow Ctrl/Cmd combinations
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      // Allow navigation and editing keys
      if (allowedKeys.includes(e.key)) {
        // Special handling for Enter key to prevent excessive newlines
        if (e.key === 'Enter') {
          // Prevent adding newlines if we're at or over the limit
          if (effectiveTextLength >= limits.maxTextLength) {
            e.preventDefault();
            return;
          }
        }
        return;
      }

      // Check if we're at the limit
      if (
        effectiveTextLength >= limits.maxTextLength ||
        currentHtmlLength >= limits.maxHtmlLength
      ) {
        // Prevent the input
        e.preventDefault();
        return;
      }

      // For single character input, check if it would exceed the limit
      if (e.key.length === 1) {
        if (
          effectiveTextLength + 1 > limits.maxTextLength ||
          currentHtmlLength + 1 > limits.maxHtmlLength
        ) {
          e.preventDefault();
          return;
        }
      }
    };
  }

  /**
   * Create paste handler for the editor
   */
  private createPasteHandler(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ) {
    return (e: ClipboardEvent) => {
      // Prevent default and stop propagation IMMEDIATELY to prevent multiple paste operations
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const currentTextLength = Math.max(0, editor.getLength() - 1); // exclude trailing newline
      const currentHtmlLength = editor.root.innerHTML.length;
      const limits = this.getEditorLimits(config, type);

      if (currentTextLength > limits.maxTextLength || currentHtmlLength > limits.maxHtmlLength) {
        return;
      }

      const data = e.clipboardData;
      if (!data) return;

      // Get the current selection
      let selection = editor.getSelection();

      // If selection is null, try to get the last known selection
      if (!selection) {
        selection = this.lastSelection.get(editor) || null;
      }

      // Use captured selection or fallback to end if no selection exists
      const insertPosition = selection || { index: currentTextLength, length: 0 };

      // Get pasted text and sanitize it to remove any HTML tags
      let pastedText = data.getData('text/plain') || '';

      // Also check for HTML content and sanitize it
      const htmlContent = data.getData('text/html');

      if (htmlContent) {
        pastedText = this.sanitizePastedText(htmlContent);
      } else {
        pastedText = this.sanitizePastedText(pastedText);
      }

      const pastedTextLength = pastedText.length;

      // Available budgets accounting for current selection replacement
      const selectedLen = insertPosition.length || 0;

      const effectiveTextBeforeInsert = Math.max(0, currentTextLength - selectedLen);
      const availableTextBudget = Math.max(0, limits.maxTextLength - effectiveTextBeforeInsert);

      // Check if we have budget for pasting
      if (availableTextBudget <= 0) return;

      const sliceLen = Math.min(pastedTextLength, availableTextBudget);
      const textPortion = (pastedText || '').slice(0, sliceLen);

      // Replace current selection and insert plain text at cursor
      editor.deleteText(insertPosition.index, insertPosition.length, 'silent');
      editor.insertText(insertPosition.index, textPortion, {}, 'user');

      // Calculate cursor position after insertion
      const cursorPosition = insertPosition.index + textPortion.length;

      // Post-guard: enforce text limit first
      let effectiveLen = Math.max(0, editor.getLength() - 1);
      if (effectiveLen > limits.maxTextLength) {
        editor.deleteText(limits.maxTextLength, effectiveLen - limits.maxTextLength, 'silent');
        effectiveLen = Math.max(0, editor.getLength() - 1);
      }

      // Then ensure HTML limit by trimming trailing characters
      let guard = 0;
      while (
        editor.root.innerHTML.length > limits.maxHtmlLength &&
        effectiveLen > 0 &&
        guard < 2000
      ) {
        editor.deleteText(effectiveLen - 1, 1, 'silent');
        effectiveLen = Math.max(0, editor.getLength() - 1);
        guard++;
      }

      // Set cursor position after the inserted text
      // Use a small delay to ensure the text insertion is complete
      setTimeout(() => {
        const finalCursorPos = Math.min(cursorPosition, Math.max(0, editor.getLength() - 1));
        editor.setSelection(finalCursorPos, 0, 'api');
        editor.focus();
      }, 10);
    };
  }

  /**
   * Create text change handler for the editor
   */
  private createTextChangeHandler(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ) {
    return (delta: any, oldDelta: any, source: any) => {
      const textLength = Math.max(0, editor.getLength() - 1);
      const limits = this.getEditorLimits(config, type);

      if (this.shouldTruncateAfterPaste && source === 'user') {
        if (textLength > this.pendingTruncationLength) {
          // Use the new truncation logic for paste operations
          this.truncateEditorContent(
            editor,
            this.pendingTruncationLength,
            limits.maxHtmlLength,
            type,
            config,
          );
        }
        this.shouldTruncateAfterPaste = false;
        this.pendingTruncationLength = 0;
      }

      // Handle regular truncation for other cases
      if (
        (textLength > limits.maxTextLength ||
          editor.root.innerHTML.length > limits.maxHtmlLength) &&
        source === 'user'
      ) {
        // Prevent scrolling during truncation by temporarily disabling scroll
        this.preventScrollDuringTruncation(editor, () => {
          this.truncateEditorContent(
            editor,
            limits.maxTextLength,
            limits.maxHtmlLength,
            type,
            config,
          );
        });
      }
    };
  }

  /**
   * Truncate editor content while preserving HTML formatting
   */
  truncateEditorContent(
    editor: Quill,
    maxTextLength: number,
    maxHtmlLength: number,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ): void {
    const textLength = Math.max(0, editor.getLength() - 1);
    const htmlContent = editor.root.innerHTML;
    const htmlLength = htmlContent.length;

    // Check if we need to truncate based on text length
    if (textLength > maxTextLength) {
      // Remove extra characters from the end until we are within the text limit
      let overBy = textLength - maxTextLength;
      if (overBy > 0) {
        editor.deleteText(maxTextLength, overBy, 'silent');
      }

      // Update the form field
      const fieldName =
        type === 'recommendedAction' ? 'recommended_corrective_action' : 'description';
      config.form.setValue({
        ...config.form.value,
        [fieldName]: editor.root.innerHTML,
      });
    }
    // Check if we need to truncate based on HTML length
    else if (htmlLength > maxHtmlLength) {
      // Reduce text by deleting tail characters until HTML length fits
      let guard = 0;
      let effectiveLen = Math.max(0, editor.getLength() - 1);
      while (editor.root.innerHTML.length > maxHtmlLength && effectiveLen > 0 && guard < 2000) {
        editor.deleteText(effectiveLen - 1, 1, 'silent');
        effectiveLen = Math.max(0, editor.getLength() - 1);
        guard++;
      }

      // Update the form field
      const fieldName =
        type === 'recommendedAction' ? 'recommended_corrective_action' : 'description';
      config.form.setValue({
        ...config.form.value,
        [fieldName]: editor.root.innerHTML,
      });
    }
  }

  /**
   * Force editor length limits
   */
  forceEditorLength(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ): void {
    if (!editor) return;

    const textLength = Math.max(0, editor.getLength() - 1);
    const htmlLength = editor.root.innerHTML.length;
    const limits = this.getEditorLimits(config, type);

    if (textLength > limits.maxTextLength || htmlLength > limits.maxHtmlLength) {
      this.truncateEditorContent(editor, limits.maxTextLength, limits.maxHtmlLength, type, config);
    }
  }

  /**
   * Get character count for text content
   */
  getTextCharCount(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ): number {
    if (!editor) {
      return 0;
    }

    if (typeof editor?.getLength === 'function') {
      try {
        const currentContent = Math.max(0, editor.getLength() - 1);
        const limits = this.getEditorLimits(config, type);
        const result = Math.min(currentContent, limits.maxTextLength);

        return result;
      } catch (error) {
        console.warn('Error getting editor content:', error);
      }
    }

    const fieldName =
      type === 'recommendedAction' ? 'recommended_corrective_action' : 'description';
    const fieldValue = config.form.get(fieldName)?.value || '';
    const textContent = fieldValue.replace(/<[^>]*>/g, '');
    const limits = this.getEditorLimits(config, type);
    const result = Math.min(textContent.length, limits.maxTextLength);

    return result;
  }

  /**
   * Get character count for HTML content
   */
  getHtmlCharCount(
    editor: Quill,
    type: 'description' | 'recommendedAction',
    config: EditorConfig,
  ): number {
    if (!editor) {
      return 0;
    }

    if (typeof editor?.root?.innerHTML === 'string') {
      try {
        const currentHtmlContent = editor.root.innerHTML;
        const limits = this.getEditorLimits(config, type);
        return Math.min(currentHtmlContent.length, limits.maxHtmlLength);
      } catch (error) {
        console.warn('Error getting editor HTML content:', error);
      }
    }

    const fieldName =
      type === 'recommendedAction' ? 'recommended_corrective_action' : 'description';
    const fieldValue = config.form.get(fieldName)?.value || '';
    const limits = this.getEditorLimits(config, type);
    return Math.min(fieldValue.length, limits.maxHtmlLength);
  }

  /**
   * Check if current language is Arabic
   */
  private isArabic(): boolean {
    const currentLang = localStorage.getItem('lang');
    return currentLang === 'ar';
  }

  /**
   * Sanitize pasted text by removing HTML tags while preserving spaces and newlines
   */
  private sanitizePastedText(text: string): string {
    if (!text) return '';

    // If the text contains HTML tags, process it
    if (text.includes('<') && text.includes('>')) {
      // First, convert structural tags to newlines to preserve formatting
      let sanitized = text
        .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines
        .replace(/<\/p>/gi, '\n\n') // Convert closing </p> tags to double newlines
        .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
        .replace(/<\/div>/gi, '\n') // Convert closing </div> tags to newlines
        .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
        .replace(/<\/li>/gi, '\n') // Convert list items to newlines
        .replace(/<[^>]+>/g, '') // Remove all remaining HTML tags
        .replace(/&nbsp;/g, ' ') // Convert &nbsp; to regular space
        .replace(/&lt;/g, '<') // Decode HTML entities
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/\n{3,}/g, '\n\n'); // Replace 3+ newlines with just 2

      return sanitized.trim();
    }

    // If no HTML tags, just decode any HTML entities that might be present
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  /**
   * Prevent scrolling during truncation operations
   */
  private preventScrollDuringTruncation(editor: Quill, callback: () => void): void {
    // Store current scroll position
    const scrollContainer = this.findScrollContainer(editor);
    const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

    // Temporarily disable scroll events
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    // Execute the truncation
    callback();

    // Restore scroll position and re-enable scrolling
    setTimeout(() => {
      if (scrollContainer) {
        scrollContainer.style.overflow = '';
        scrollContainer.scrollTop = scrollTop;
      }
    }, 0);
  }

  /**
   * Find the scroll container for the editor
   */
  private findScrollContainer(editor: Quill): HTMLElement | null {
    let element = editor.root;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      if (
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll'
      ) {
        return element as HTMLElement;
      }
      element = element.parentElement;
    }
    return null;
  }

  /**
   * Clean up editor event listeners
   */
  cleanupEditor(editor: Quill): void {
    if (!editor) return;
    const anyEditor: any = editor as any;
    if (anyEditor?.__whHandlers) {
      try {
        editor.root?.removeEventListener('paste', anyEditor.__whHandlers.paste);
        editor.root?.removeEventListener('keydown', anyEditor.__whHandlers.keydown);
        editor.off('text-change', anyEditor.__whHandlers.textChange);
        editor.off('selection-change', anyEditor.__whHandlers.selectionChange);
        anyEditor.__whHandlers = null;
      } catch {}
    }
    // Restore original clipboard onPaste if it was overridden
    if (anyEditor?.__originalClipboardOnPaste) {
      const clipboard = anyEditor.getModule('clipboard');
      if (clipboard) {
        clipboard.onPaste = anyEditor.__originalClipboardOnPaste;
      }
      anyEditor.__originalClipboardOnPaste = null;
    }
    // Clean up stored selection
    this.lastSelection.delete(editor);
  }

  /**
   * Reset truncation state
   */
  resetTruncationState(): void {
    this.shouldTruncateAfterPaste = false;
    this.pendingTruncationLength = 0;
  }
}
